import React, { useRef, useState } from 'react';
import { CameraIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { CollaboratorAvatar } from '@/features/Collaborators/Shared';
import { usePartialUpdateCollaboratorMutation } from '@/features/Collaborators/Data/hooks/useCollaboratorMutations';
import { useToast } from '@/hooks/useToast';
import { uploadFile } from '@/utils/storage';

const TAMANHO_MAXIMO_BYTES = 5 * 1024 * 1024;

interface AvatarEditavelProps {
    collaboratorId: string;
    name: string;
    photoUrl?: string | null;
}

/**
 * Foto do perfil com "Trocar foto" — pedido do RH: não existia nenhum jeito de trocar a foto
 * (as atuais vieram da antiga sincronização com o Google Workspace, a da Microsoft não traz
 * foto). Só aparece na edição do perfil, que já é restrita a quem gerencia colaboradores.
 * A troca é imediata (não depende do "Salvar" do formulário — que por isso não envia
 * `photoUrl`, ver CollaboratorEditPage).
 */
export default function AvatarEditavel({ collaboratorId, name, photoUrl }: AvatarEditavelProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [enviando, setEnviando] = useState(false);
    // Mostra a foto nova na hora, sem esperar a cópia pública do perfil ser atualizada.
    const [fotoNova, setFotoNova] = useState<string | null>(null);
    const queryClient = useQueryClient();
    const atualizar = usePartialUpdateCollaboratorMutation();
    const toast = useToast();

    const trocarFoto = async (arquivo: File) => {
        if (!arquivo.type.startsWith('image/')) {
            toast.error('Arquivo inválido', 'Escolha uma imagem (JPG ou PNG).');
            return;
        }
        if (arquivo.size > TAMANHO_MAXIMO_BYTES) {
            toast.error('Imagem muito grande', 'Escolha uma imagem de até 5 MB.');
            return;
        }
        setEnviando(true);
        try {
            const extensao = arquivo.name.split('.').pop()?.toLowerCase() || 'jpg';
            const url = await uploadFile(arquivo, `collaborators/${collaboratorId}/photo/foto_${Date.now()}.${extensao}`);
            await atualizar.mutateAsync({ id: collaboratorId, data: { photoUrl: url } as any });
            setFotoNova(url);
            queryClient.invalidateQueries({ queryKey: ['collaborator_public_detail', collaboratorId] });
            toast.success('Foto atualizada');
        } catch (erro: any) {
            toast.error('Não foi possível trocar a foto', erro?.message || 'Tente novamente.');
        } finally {
            setEnviando(false);
        }
    };

    return (
        <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={enviando}
            className="group relative block rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500"
            title="Trocar foto"
        >
            <CollaboratorAvatar name={name} photoUrl={fotoNova ?? photoUrl} size="xlarge" />
            <span
                className={`absolute inset-0 flex flex-col items-center justify-center rounded-full bg-black/50 text-white transition-opacity ${
                    enviando ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`}
            >
                <CameraIcon className={`h-6 w-6 ${enviando ? 'animate-pulse' : ''}`} />
                <span className="text-[10px] font-semibold">{enviando ? 'Enviando...' : 'Trocar foto'}</span>
            </span>
            {/* Sempre visível (no celular não existe "passar o mouse"). */}
            <span className="absolute bottom-0 right-0 flex h-7 w-7 items-center justify-center rounded-full bg-indigo-600 text-white shadow ring-2 ring-white">
                <CameraIcon className="h-4 w-4" />
            </span>
            <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => {
                    const arquivo = e.target.files?.[0];
                    e.target.value = '';
                    if (arquivo) trocarFoto(arquivo);
                }}
            />
        </button>
    );
}
